import { Component, inject } from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import Utils from '../utils/utils';
import {environment} from '../../environments/environment';
import {ILogin, Login} from '../../shared/model/login.model';
import { MatCard, MatCardTitle } from '@angular/material/card';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.css'],
    imports: [MatCard, MatCardTitle, FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatError, MatButton, MatProgressSpinner]
})
export class ResetPasswordComponent {
  private http = inject(HttpClient);
  private fb = inject(UntypedFormBuilder);
  private router = inject(Router);
  private toastr = inject(ToastrService);


  form: UntypedFormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    confirmationCode: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
    password: ['', [Validators.required, Validators.minLength(8)]],
    passwordConfirm: ['', [Validators.required]]
  }, {
    validator: Utils.passwordMatchValidator
  });
  error = '';
  step = 1;
  isLoading = false;
  apiUrl = environment.apiUrl;
  login: ILogin = new Login();

  showSuccess(message: string): void {
    this.toastr.success(message, 'OK', {
      timeOut: 3000
    });
  }

  showError(error: string): void {
    this.toastr.error(error, 'Erreur', {
      timeOut: 3000
    });
  }

  public sendResetPassword(): void {
    this.isLoading = true;
    this.http.post(this.apiUrl + '/auth/change-password', this.login, {observe: 'response', responseType: 'text'})
      .subscribe(() => {
          this.step = 2;
          this.isLoading = false;
        }, (err: HttpErrorResponse) => {
          const errorCode = err.error;
          switch (errorCode) {
            case 'ERR_MAIL_NOT_SENT':
              this.error = 'Impossible d\'envoyer le mail de confirmation, merci de réessayer dans quelques minutes';
              break;

            case 'ERR_ACC_NOT_EXIST':
              this.error = 'Ce compte n\'existe pas';
              break;
            case 'ERR_ACCOUNT_IS_INEXWEB':
              this.error = 'Votre compte est géré par Inexweb';
              break;
            default:
              this.error = 'Merci de réessayer dans quelques instants';
              break;
          }
          this.showError(this.error);
          this.isLoading = false;
        }
      );
  }

  public validConfirmationCode(): void {
    this.http.post(this.apiUrl + '/auth/check-activation-code-password', this.login, {
      observe: 'response',
      responseType: 'text'
    })
      .subscribe(() => {
          this.step = 3;
        }, (err: HttpErrorResponse) => {
          const errorCode = err.error;
          switch (errorCode) {
            case 'ERR_BAD_ACTIVATION_CODE':
              this.error = 'Code de confirmation invalide';
              break;

            case 'ERR_ACC_NOT_EXIST':
              this.error = 'Ce compte n\'existe pas';
              break;

            case 'ERR_EXPIRED_CODE':
              this.error = 'Le code de validation est expiré, un nouveau code vient de vous être envoyé par email';
              this.sendResetPassword();
              break;

            default:
              this.error = 'Merci de réessayer dans quelques instants';
              break;
          }
          this.showError(this.error);
        }
      );
  }

  public resendConfirmationCode(): void {
    this.isLoading = true;
    this.login.typeMail = 'password';
    this.http.post(this.apiUrl + '/auth/resend-code', this.login, {observe: 'response', responseType: 'text'})
      .subscribe(() => {
        this.showSuccess('Un nouveau code vient de vous être envoyé sur votre adresse email');
        this.isLoading = false;
      }, (error: HttpErrorResponse) => {
        const errorCode = error.error;
        switch (errorCode) {
          case 'ERR_MAIL_NOT_RESENT':
            this.error = 'Impossible de renvoyer le mail de confirmation, merci de réessayer dans quelques minutes';
            break;

          case 'ERR_USER_NOT_FOUND':
            this.error = 'L\'utilisateur n\'existe pas';
            break;

          default:
            this.error = 'Merci de réessayer dans quelques instants';
            break;
        }
        this.showError(this.error);
        this.isLoading = false;
      });
  }

  public changePassword(): void {
    this.isLoading = true;
    this.http.post(this.apiUrl + '/auth/password-validation', this.login, {observe: 'response', responseType: 'text'})
      .subscribe(() => {
          this.router.navigate(['login']);
          this.showSuccess('Vous avez bien changé le mot de passe de votre compte. Vous pouvez désormais vous connecter avec celui-ci');
          this.isLoading = false;
        }, (err: HttpErrorResponse) => {
          const errorCode = err.error;
          switch (errorCode) {
            case 'ERR_EXPIRED_CODE':
              this.error = 'Code de validation expiré';
              break;

            case 'ERR_BAD_ACTIVATION_CODE':
              this.error = 'Code de confirmation invalide';
              break;

            case 'ERR_NO_ACTIVATION_CODE':
              this.error = 'Code de confirmation manquant';
              break;

            case 'ERR_ACC_NOT_EXIST':
              this.error = 'Le compte n\'existe pas';
              break;

            default:
              this.error = 'Merci de réessayer dans quelques instants';
              break;
          }
          this.showError(this.error);
          this.isLoading = false;
        }
      );
  }

}
